import { h, defineComponent, ref, computed, watch } from 'vue';
import { Modal, modalProps, ModalProps } from '@/ui/components/Modal';
import { Button } from '@/ui/components/Button';
import { pageSubtitle } from '@/ui/components';
import { numberInput } from '@/ui/components/form/NumberInput';
import { fetchUser, findQuota, user } from '@/ui/auth/auth';
import { toasted } from '@/ui/helpers';
import { t } from '@/ui/i18n/i18n';
import { Icon } from '@/ui/components/icon/Icon';
import axios from 'axios';
import { updateSubscription } from '@/services/api/resources/chargebee/subscription/actions';

export const UpdateUserSeatsModal = defineComponent<ModalProps>({
  name: 'UpdateUserSeatsModal',
  props: modalProps,
  setup(props, context) {
    const maxNumberOfUserSeats = ref(1);
    const currentUsedUsersSeats = ref(1);

    const fetchUserQuotas = () => {
      maxNumberOfUserSeats.value = findQuota('user')!.max;
      currentUsedUsersSeats.value = findQuota('user')!.current;
    };

    fetchUserQuotas();

    const numberSeats = ref(maxNumberOfUserSeats);

    const closeModal = () => context.emit('close');

    const updatingSubscription = ref(false);

    watch([user], fetchUserQuotas);

    const updateUserSubscription = async () => {
      updatingSubscription.value = true;

      // We are using legacy here since we will be refactoring the subscription.
      // When refactor the subscription logic we can refactor this part
      try {
        await updateSubscription(user.value.subscription!.id, numberSeats.value)();
        toasted.success(t('update-user-modal.seats-updated', { numberUserSeats: numberSeats.value.toString() }));
        context.emit('created:user-seat');
        closeModal();
        await fetchUser()();
      } catch (e) {
        if (axios.isAxiosError(e)) {
          toasted.error(e.response?.data.message);
        } else {
          toasted.error(t('common.error'));
          console.error(e);
        }
      }

      updatingSubscription.value = false;
    };

    const canReduceUsersCount = computed(() => numberSeats.value <= currentUsedUsersSeats.value && numberSeats.value > 1);

    return function render() {
      const updateSeatsModalContent = () => [
        pageSubtitle(t('update-user-modal.title')),
        h('div', t('update-user-modal.subtitle')),
        h('div', { class: 'update-user-modal__input-row' }, [
          h('div', { class: 'has-text-weight-semibold' }, t('update-user-modal.seats')),
          numberInput({
            value: numberSeats.value,
            min: currentUsedUsersSeats.value,
            controlsPosition: 'compact',
            size: 'is-small',
            controlsRounded: true,
          }, v => {
            numberSeats.value = v;
          }),
        ]),
        h('div', { class: 'update-user-modal__info-row' }, [
          canReduceUsersCount.value ? h('div', { class: 'update-user-modal__info-item' }, [
            h(Icon, {
              props: {
                iconPack: 'fal',
                icon: 'info-circle',
                size: '1x',
              },
            }),
            t('update-user-modal.remove-users'),
          ]) : null,
          h('div', [
            h(Icon, {
              props: {
                iconPack: 'fal',
                icon: 'info-circle',
                size: '1x',
              },
            }),
            t('update-user-modal.info'),
          ]),
        ]),
      ];

      return h(Modal, {
        props: {
          ...props,
          customClass: 'update-user-modal',
          width: '465px',
          ariaRole: 'dialog',
          trapFocus: true,
          destroyOnHide: false,
          ariaModal: true,
        },
        scopedSlots: {
          actions: () => h('div', [
            h(Button, {
              props: { type: 'is-text', btnClass: 'mr-2 has-text-weight-semibold' },
              on: { click: closeModal },
            }, t('modal.cancel')),
            h(Button, {
              props: {
                loading: updatingSubscription.value,
                disabled: numberSeats.value < 1 || numberSeats.value < currentUsedUsersSeats.value,
              },
              on: {
                click: updateUserSubscription,
              },
            }, t('common.update')),
          ]),
        },
        on: {
          close: closeModal,
        },
      }, updateSeatsModalContent());
    };
  },
});
